import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import educentialApi from '../../api/educentialApi';
import {
  ArrowLeft,
  User,
  Mail,
  Globe,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Download,
  AlertCircle,
  Phone,
  CreditCard,
  GraduationCap,
  BookOpen,
  Award,
  Languages,
  MapPin,
  Clock,
  Eye
} from 'lucide-react';

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingDocId, setRejectingDocId] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');

  useEffect(() => {
    loadApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      const response = await educentialApi.get(`/api/admin/applications/${id}`);
      setApplication(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to load application:', err);
      setError(err.response?.data?.error || 'Failed to load application details');

      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('Application not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === application.status) return;
    setPendingStatus(newStatus);
    setShowStatusModal(true);
  };

  const confirmStatusUpdate = async () => {
    try {
      setUpdating(true);
      setShowStatusModal(false);
      await educentialApi.patch(`/api/admin/applications/${id}/status`, {
        status: pendingStatus,
      });
      await loadApplication();
    } catch (err) {
      console.error('Failed to update status:', err);
      alert(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
      setPendingStatus(null);
    }
  };

  const cancelStatusUpdate = () => {
    setShowStatusModal(false);
    setPendingStatus(null);
  };

  const reviewDocument = async (documentId, status, rejectionNote = null) => {
    try {
      await educentialApi.patch(`/api/admin/documents/${documentId}/review`, {
        status,
        rejectionNote,
      });
      await loadApplication();
    } catch (err) {
      console.error('Failed to review document:', err);
      alert(err.response?.data?.error || 'Failed to review document');
    }
  };

  const handleRejectDocument = (documentId) => {
    setRejectingDocId(documentId);
    setRejectionNote('');
    setShowRejectModal(true);
  };

  const confirmRejectDocument = async () => {
    if (!rejectionNote.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setShowRejectModal(false);
    await reviewDocument(rejectingDocId, 'REJECTED', rejectionNote);
    setRejectingDocId(null);
    setRejectionNote('');
  };

  const cancelRejectDocument = () => {
    setShowRejectModal(false);
    setRejectingDocId(null);
    setRejectionNote('');
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', dot: 'bg-gray-500' },
      SUBMITTED: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', dot: 'bg-blue-500' },
      UNDER_REVIEW: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', dot: 'bg-yellow-500' },
      OFFER_SECURED: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', dot: 'bg-green-500' },
      VISA_IN_PROGRESS: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', dot: 'bg-purple-500' },
      COMPLETED: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', dot: 'bg-teal-500' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', dot: 'bg-red-500' }
    };
    return colors[status] || colors.DRAFT;
  };

  const getDocStatusBadge = (status) => {
    const badges = {
      PENDING: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Clock },
      APPROVED: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle },
      REJECTED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: XCircle }
    };
    return badges[status] || badges.PENDING;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(2) + ' MB';
  };

  if (loading) {
    return (
      <AdminLayout title="Application Details">
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading application...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Application Details">
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="bg-white border-2 border-red-200 rounded-xl p-8 max-w-md shadow-lg">
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">Error Loading Application</h3>
            <p className="text-gray-600 text-center mb-6">{error}</p>
            <button
              onClick={() => navigate('/admin/applications')}
              className="w-full px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors font-medium"
            >
              Back to Applications
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const statusColor = getStatusColor(application.status);

  return (
    <AdminLayout title={`Application #${application.id}`}>
      <div className="p-6 space-y-6">
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <button
                onClick={() => navigate('/admin/applications')}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors mt-1"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-3xl font-bold">Application #{application.id}</h1>
                  <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${statusColor.bg} ${statusColor.text} border-2 ${statusColor.border}`}>
                    {application.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-blue-100 flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>Submitted on {new Date(application.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </p>
                <p className="text-blue-100 flex items-center space-x-2 mt-1">
                  <Globe className="w-4 h-4" />
                  <span className="font-semibold">{application.country}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <span>Personal Information</span>
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoItem icon={User} label="Full Name" value={application.personalInfo?.fullName} />
                  <InfoItem icon={Calendar} label="Date of Birth" value={application.personalInfo?.dateOfBirth} />
                  <InfoItem icon={CreditCard} label="Passport Number" value={application.personalInfo?.passportNumber} />
                  <InfoItem icon={Phone} label="Phone Number" value={application.personalInfo?.phoneNumber} />
                  <InfoItem icon={MapPin} label="Nationality" value={application.personalInfo?.nationality} />
                  <InfoItem icon={Mail} label="Email Address" value={application.userEmail} className="md:col-span-2" />
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-green-600" />
                  </div>
                  <span>Academic Background</span>
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoItem icon={Award} label="Highest Qualification" value={application.academicDocuments?.highestQualification} />
                  <InfoItem icon={BookOpen} label="Field of Study" value={application.academicDocuments?.fieldOfStudy} />
                  <InfoItem icon={GraduationCap} label="Institution" value={application.academicDocuments?.institution} />
                  <InfoItem icon={Calendar} label="Graduation Year" value={application.academicDocuments?.graduationYear} />
                  <InfoItem icon={Award} label="GPA" value={application.academicDocuments?.gpa} />
                  <InfoItem icon={BookOpen} label="Intended Course" value={application.personalInfo?.intendedCourse} />
                </div>
              </div>
            </div>

            {/* English Proficiency */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Languages className="w-5 h-5 text-purple-600" />
                  </div>
                  <span>English Proficiency</span>
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <InfoItem icon={FileText} label="Test Type" value={application.englishProficiency?.testType} />
                  <InfoItem icon={Award} label="Overall Score" value={application.englishProficiency?.score} />
                  <InfoItem icon={Calendar} label="Test Date" value={application.englishProficiency?.testDate} />
                  <div className="col-span-2 md:col-span-1"></div>
                  <InfoItem label="Listening" value={application.englishProficiency?.listening} />
                  <InfoItem label="Reading" value={application.englishProficiency?.reading} />
                  <InfoItem label="Writing" value={application.englishProficiency?.writing} />
                  <InfoItem label="Speaking" value={application.englishProficiency?.speaking} />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span>Uploaded Documents</span>
                  <span className="ml-auto text-sm font-normal text-gray-600">
                    {application.documents?.length || 0} file(s)
                  </span>
                </h2>
              </div>
              <div className="p-6">
                {application.documents && application.documents.length > 0 ? (
                  <div className="space-y-4">
                    {application.documents.map((doc) => {
                      const badge = getDocStatusBadge(doc.status);
                      const StatusIcon = badge.icon;
                      return (
                        <div
                          key={doc.id}
                          className="flex items-start justify-between p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all"
                        >
                          <div className="flex items-start space-x-4 flex-1">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-900 font-semibold text-lg">{doc.documentType}</p>
                              <p className="text-gray-600 text-sm mt-1">{doc.fileName}</p>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center space-x-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <FileText className="w-3 h-3" />
                                  <span>{formatFileSize(doc.fileSize)}</span>
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 ml-4">
                            <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 ${badge.bg} ${badge.text} ${badge.border} flex items-center space-x-1`}>
                              <StatusIcon className="w-3.5 h-3.5" />
                              <span>{doc.status}</span>
                            </span>
                            <a
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300"
                              title="View document"
                            >
                              <Eye className="w-5 h-5 text-gray-700" />
                            </a>
                            {doc.status === 'PENDING' && (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => reviewDocument(doc.id, 'APPROVED')}
                                  className="p-2.5 bg-green-100 hover:bg-green-200 border-2 border-green-300 rounded-lg transition-colors"
                                  title="Approve document"
                                >
                                  <CheckCircle className="w-5 h-5 text-green-700" />
                                </button>
                                <button
                                  onClick={() => handleRejectDocument(doc.id)}
                                  className="p-2.5 bg-red-100 hover:bg-red-200 border-2 border-red-300 rounded-lg transition-colors"
                                  title="Reject document"
                                >
                                  <XCircle className="w-5 h-5 text-red-700" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No documents uploaded yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Update */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Update Status</h3>
              </div>
              <div className="p-6">
                <select
                  value={application.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updating}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="UNDER_REVIEW">Under Review</option>
                  <option value="OFFER_SECURED">Offer Secured</option>
                  <option value="VISA_IN_PROGRESS">Visa in Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                {updating && (
                  <p className="text-sm text-gray-600 mt-3 flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600"></div>
                    <span>Updating...</span>
                  </p>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Quick Info</h3>
              </div>
              <div className="p-6 space-y-4">
                <QuickInfoItem icon={Globe} label="Country" value={application.country} color="blue" />
                <QuickInfoItem icon={Calendar} label="Last Updated" value={new Date(application.updatedAt).toLocaleDateString()} color="green" />
                <QuickInfoItem icon={FileText} label="Documents" value={`${application.documents?.length || 0} uploaded`} color="purple" />
                <QuickInfoItem icon={Clock} label="Days Since Submission" value={Math.floor((new Date() - new Date(application.createdAt)) / (1000 * 60 * 60 * 24)) + ' days'} color="yellow" />
              </div>
            </div>

            {/* Student Details */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Student Details</h3>
              </div>
              <div className="p-6 space-y-4">
                <QuickInfoItem icon={User} label="User ID" value={`#${application.userId}`} color="indigo" />
                <QuickInfoItem icon={Mail} label="Email" value={application.userEmail} color="red" />
              </div>
            </div>
          </div>
        </div>

        {/* Status Confirmation Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                <h3 className="text-xl font-bold text-white flex items-center space-x-2">
                  <AlertCircle className="w-6 h-6" />
                  <span>Confirm Status Change</span>
                </h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 text-base mb-6">
                  Are you sure you want to change the application status to{' '}
                  <span className="font-bold text-gray-900">
                    {pendingStatus?.replace(/_/g, ' ')}
                  </span>
                  ?
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={cancelStatusUpdate}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors font-semibold border-2 border-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmStatusUpdate}
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold shadow-lg"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Rejection Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5">
                <h3 className="text-xl font-bold text-white flex items-center space-x-2">
                  <XCircle className="w-6 h-6" />
                  <span>Reject Document</span>
                </h3>
              </div>
              <div className="p-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason for Rejection *
                </label>
                <textarea
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this document..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={4}
                  autoFocus
                />
                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={cancelRejectDocument}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors font-semibold border-2 border-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRejectDocument}
                    className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold shadow-lg"
                  >
                    Reject Document
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// Helper Components
const InfoItem = ({ icon: Icon, label, value, className = '' }) => (
  <div className={`border-2 border-gray-200 rounded-lg p-4 bg-gray-50 hover:border-blue-300 transition-colors ${className}`}>
    <div className="flex items-center space-x-2 mb-2">
      {Icon && <Icon className="w-4 h-4 text-gray-500" />}
      <p className="text-gray-600 text-xs font-bold uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-gray-900 text-base font-semibold">{value || 'N/A'}</p>
  </div>
);

const QuickInfoItem = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div className="flex items-center space-x-3">
      <div className={`w-11 h-11 rounded-lg ${colorClasses[color]} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wide">{label}</p>
        <p className="text-gray-900 font-semibold text-sm truncate">{value}</p>
      </div>
    </div>
  );
};
